import {
  CAR_UPLOAD_PREFIX,
  fileNameFromCarImageHref,
  isLocalCarUploadPath,
  supabasePublicCarImageUrl,
} from "@shared/car-image-storage";

const SUPABASE_URL =
  (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/$/, "") ??
  "https://tgeyajxlauqhyzzaluzm.supabase.co";

const STORAGE_BUCKET = "car-images";
const SUPABASE_CAR_PUBLIC_RE =
  /\/storage\/v1\/object\/public\/car-images\/(.+)$/i;
const SUPABASE_CAR_RENDER_RE =
  /\/storage\/v1\/render\/image\/public\/car-images\/([^?]+)/i;

function supabasePublicCarUrl(fileName: string): string {
  return supabasePublicCarImageUrl(SUPABASE_URL, fileName);
}

const USER_UPLOAD_PREFIX = "/uploads/users/";

function toCarUploadPath(url: string): string | null {
  if (url.startsWith(CAR_UPLOAD_PREFIX)) return url;
  if (url.startsWith("uploads/cars/")) return `/${url}`;
  const key = fileNameFromCarImageHref(url);
  if (key && !key.includes("/")) {
    return `${CAR_UPLOAD_PREFIX}${key}`;
  }
  return null;
}

function toUserUploadPath(url: string): string | null {
  if (url.startsWith(USER_UPLOAD_PREFIX)) return url;
  if (url.startsWith("uploads/users/")) return `/${url}`;
  const fromSupabase = fileNameFromSupabaseUrl(url);
  if (fromSupabase?.startsWith("users/")) return `/uploads/${fromSupabase}`;
  return null;
}

function fileNameFromCarPath(carPath: string): string {
  return carPath.slice(CAR_UPLOAD_PREFIX.length);
}

export type CarImageDisplayPreset = "card" | "thumb" | "full";

/** How wide the slot is, so the browser does not assume every photo is full-screen. */
export const CAR_IMAGE_SLOT_SIZES = {
  viewport: "100vw",
  content: "(min-width: 1280px) 960px, (min-width: 768px) 80vw, 100vw",
  panel: "(min-width: 1024px) 640px, 100vw",
  dialog: "(min-width: 768px) 720px, 92vw",
} as const;

type PresetSpec = {
  width: number;
  height: number;
  quality: number;
  srcSetWidths: number[];
  sizes?: string;
};

/** High-quality display targets · 2× retina for typical listing card widths. */
const PRESET_SPECS: Record<CarImageDisplayPreset, PresetSpec> = {
  thumb: {
    width: 480,
    height: 360,
    quality: 90,
    srcSetWidths: [240, 480, 720],
    sizes: "112px",
  },
  card: {
    width: 1600,
    height: 1200,
    quality: 92,
    srcSetWidths: [800, 1200, 1600, 1920],
    sizes: "(min-width: 1280px) 400px, (min-width: 768px) 50vw, 100vw",
  },
  full: {
    width: 1920,
    height: 1440,
    quality: 92,
    srcSetWidths: [960, 1440, 1920],
    sizes: CAR_IMAGE_SLOT_SIZES.content,
  },
};

/** Supabase is the source of truth whenever the project is wired to it (prod or local dev). */
function preferRemoteCarStorageFirst(): boolean {
  return (
    import.meta.env.PROD ||
    import.meta.env.VITE_CAR_IMAGES_STORAGE === "supabase" ||
    Boolean(import.meta.env.VITE_SUPABASE_URL)
  );
}

function addCarPathCandidates(out: string[], carPath: string) {
  const fileName = fileNameFromCarPath(carPath);
  const remote = supabasePublicCarUrl(fileName);

  if (preferRemoteCarStorageFirst()) {
    pushUnique(out, remote);
    pushUnique(out, carPath);
    return;
  }

  pushUnique(out, carPath);
  pushUnique(out, remote);
}

function pushUnique(out: string[], value: string) {
  if (value && !out.includes(value)) out.push(value);
}

function fileNameFromSupabaseUrl(url: string): string | null {
  const objectMatch = url.match(SUPABASE_CAR_PUBLIC_RE);
  if (objectMatch?.[1]) return decodeURIComponent(objectMatch[1]);

  const renderMatch = url.match(SUPABASE_CAR_RENDER_RE);
  if (renderMatch?.[1]) return decodeURIComponent(renderMatch[1]);

  return null;
}

/** Resolve a candidate URL to the canonical Supabase public object URL. */
function resolveSupabasePublicUrl(candidate: string): string | null {
  const fromUrl = fileNameFromSupabaseUrl(candidate);
  if (fromUrl) return supabasePublicCarUrl(fromUrl);

  const carPath = toCarUploadPath(candidate);
  if (carPath) return supabasePublicCarUrl(fileNameFromCarPath(carPath));

  if (candidate.startsWith(USER_UPLOAD_PREFIX) || candidate.startsWith("uploads/users/")) {
    const userPath = candidate.startsWith("/") ? candidate : `/${candidate}`;
    const fileName = userPath.slice(USER_UPLOAD_PREFIX.length);
    if (fileName) return supabasePublicCarUrl(`users/${fileName}`);
  }

  return null;
}

const SUPABASE_IMAGE_TRANSFORM_ENABLED =
  import.meta.env.VITE_SUPABASE_IMAGE_TRANSFORM === "true";

/** Supabase image transforms (render API) · off by default until enabled on the project. */
export function isSupabaseImageTransformEnabled(): boolean {
  return SUPABASE_IMAGE_TRANSFORM_ENABLED;
}

export function isLocalCarImagePath(url: string | null | undefined): boolean {
  return isLocalCarUploadPath(url);
}

/** True when an HTTP response is a stored file, not the SPA HTML fallback. */
export function isReachableImageResponse(res: Response): boolean {
  if (!res.ok) return false;
  const ct = (res.headers.get("content-type") ?? "").toLowerCase();
  if (ct.startsWith("text/html")) return false;
  return true;
}

/** Skip a second storage probe when editing and the main image URL did not change. */
export function shouldRecheckMainImageOnSave(opts: {
  isEdit: boolean;
  originalMain?: string | null;
  currentMain: string;
}): boolean {
  const current = opts.currentMain.trim();
  if (!current) return false;
  if (opts.isEdit && (opts.originalMain ?? "").trim() === current) return false;
  return true;
}

async function probeImageUrl(candidate: string): Promise<boolean> {
  try {
    const head = await fetch(candidate, { method: "HEAD", cache: "no-store" });
    if (isReachableImageResponse(head)) return true;
    if (head.status !== 405 && head.status !== 0) return false;
  } catch {
    // HEAD is often blocked by CORS; GET still works for public objects.
  }

  try {
    const getRes = await fetch(candidate, {
      method: "GET",
      cache: "no-store",
      headers: { Range: "bytes=0-0" },
    });
    return isReachableImageResponse(getRes);
  } catch {
    return false;
  }
}

/** Probe the first loadable URL for a car image path. Rejects HTML SPA fallbacks. */
export async function verifyCarImageReachable(url: string | null | undefined): Promise<boolean> {
  const candidates = getCarImageUrlCandidates(url);
  for (const candidate of candidates) {
    if (candidate.startsWith("blob:") || candidate.startsWith("data:")) return true;
    if (
      import.meta.env.PROD &&
      (candidate.startsWith(CAR_UPLOAD_PREFIX) || candidate.startsWith(USER_UPLOAD_PREFIX))
    ) {
      continue;
    }
    if (await probeImageUrl(candidate)) return true;
  }
  return false;
}

function toSupabaseRenderUrl(
  publicObjectUrl: string,
  opts: { width: number; height: number; quality: number },
): string | null {
  if (!publicObjectUrl.includes("/storage/v1/object/public/")) return null;

  const renderBase = publicObjectUrl
    .split("?")[0]
    .replace("/storage/v1/object/public/", "/render/image/public/");
  const params = new URLSearchParams({
    width: String(opts.width),
    height: String(opts.height),
    quality: String(opts.quality),
    resize: "cover",
  });
  return `${renderBase}?${params}`;
}

function buildSrcSet(publicObjectUrl: string, spec: PresetSpec): string | undefined {
  const parts: string[] = [];

  for (const width of spec.srcSetWidths) {
    const height = Math.round(width * (spec.height / spec.width));
    const url = toSupabaseRenderUrl(publicObjectUrl, {
      width,
      height,
      quality: spec.quality,
    });
    if (url) parts.push(`${url} ${width}w`);
  }

  return parts.length ? parts.join(", ") : undefined;
}

export type CarImageDisplayCandidate = {
  src: string;
  srcSet?: string;
  sizes?: string;
};

function pushUniqueCandidate(out: CarImageDisplayCandidate[], candidate: CarImageDisplayCandidate) {
  if (!candidate.src) return;
  if (!out.some((row) => row.src === candidate.src)) out.push(candidate);
}

/**
 * Ordered image sources to try (optimized Supabase render URL first, then original).
 */
export function getCarImageDisplayCandidates(
  url: string | null | undefined,
  preset: CarImageDisplayPreset = "full",
): CarImageDisplayCandidate[] {
  const bases = getCarImageUrlCandidates(url);
  const spec = PRESET_SPECS[preset];
  const out: CarImageDisplayCandidate[] = [];

  for (const base of bases) {
    if (base.startsWith("blob:") || base.startsWith("data:")) {
      pushUniqueCandidate(out, { src: base });
      continue;
    }

    const publicUrl = resolveSupabasePublicUrl(base);
    if (publicUrl) {
      // Supabase render/image requires the Pro image-transform add-on; without it
      // every transform URL returns 403 and breaks cards before the fallback runs.
      if (SUPABASE_IMAGE_TRANSFORM_ENABLED) {
        const optimized = toSupabaseRenderUrl(publicUrl, {
          width: spec.width,
          height: spec.height,
          quality: spec.quality,
        });
        const srcSet = buildSrcSet(publicUrl, spec);

        if (optimized) {
          pushUniqueCandidate(out, {
            src: optimized,
            srcSet,
            sizes: spec.sizes,
          });
        }
      }
      pushUniqueCandidate(out, { src: publicUrl, sizes: spec.sizes });
      const localCarPath = toCarUploadPath(base);
      if (localCarPath) {
        pushUniqueCandidate(out, { src: localCarPath });
      }
      const localUserPath = toUserUploadPath(base) ?? toUserUploadPath(publicUrl);
      if (localUserPath) {
        pushUniqueCandidate(out, { src: localUserPath });
      }
      continue;
    }

    const localPath = toCarUploadPath(base);
    if (localPath) {
      pushUniqueCandidate(out, { src: localPath });
      continue;
    }

    pushUniqueCandidate(out, { src: base });
  }

  return out;
}

/**
 * Ordered URLs to try when loading a car image (Supabase public URL first when configured).
 */
export function getCarImageUrlCandidates(url: string | null | undefined): string[] {
  if (!url?.trim()) return [];

  const trimmed = url.trim();
  const out: string[] = [];

  if (trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
    pushUnique(out, trimmed);
    return out;
  }

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    pushUnique(out, trimmed);
    const supabaseMatch = trimmed.match(SUPABASE_CAR_PUBLIC_RE);
    if (supabaseMatch?.[1]) {
      const decoded = decodeURIComponent(supabaseMatch[1]);
      if (decoded.startsWith("users/")) {
        pushUnique(out, `/uploads/${decoded}`);
      } else {
        pushUnique(out, `${CAR_UPLOAD_PREFIX}${decoded}`);
      }
    }
    return out;
  }

  const carPath = toCarUploadPath(trimmed);
  if (carPath) {
    addCarPathCandidates(out, carPath);
    return out;
  }

  if (trimmed.startsWith(USER_UPLOAD_PREFIX) || trimmed.startsWith("uploads/users/")) {
    const userPath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    const fileName = userPath.slice(USER_UPLOAD_PREFIX.length);
    if (!fileName) {
      pushUnique(out, userPath);
      return out;
    }
    const remote = supabasePublicCarUrl(`users/${fileName}`);

    if (preferRemoteCarStorageFirst()) {
      pushUnique(out, remote);
      pushUnique(out, userPath);
    } else {
      pushUnique(out, userPath);
      pushUnique(out, remote);
    }
    return out;
  }

  pushUnique(out, trimmed.startsWith("/") ? trimmed : `/${trimmed}`);
  return out;
}

/**
 * Primary car image URL (first display candidate). Use {@link getCarImageDisplayCandidates}
 * with {@link CarImage} when you need automatic fallback and retina srcset.
 */
export function resolveCarImageUrl(
  url: string | null | undefined,
  preset: CarImageDisplayPreset = "full",
): string {
  return getCarImageDisplayCandidates(url, preset)[0]?.src ?? "";
}
